// OutlierVisualizer
// Owns a Group of wireframe cubes, one per tile. Built lazily on first
// enable() call so the default startup path costs zero scene graph
// nodes. Disable() clears Group children and disposes the line geometry
// to keep memory tidy across long sessions where the user might toggle
// many times.
//
// Color contract (schema v4+):
//   - blue: inlier tiles (tile.isOutlier === false)
//   - red:  outlier tiles (tile.isOutlier === true)
//
// The signal is tile.isOutlier — set by TileRegistry from the manifest's
// per-tile "outlier:true" flag (added in schema v4). We deliberately do
// NOT switch on tile.inTrimmedBounds for color: that flag is a legacy
// derivation (does this tile's bbox intersect the trimmed scene bounds?)
// which mostly agrees with isOutlier but can disagree at the edges —
// e.g. an inlier tile whose bbox just barely extends past the trimmed
// bounds would render red under the old rule, which isn't what the user
// wants.

import {
  Group, BoxGeometry, EdgesGeometry, LineSegments, LineBasicMaterial,
  Vector3,
} from 'three';

const COLOR_INLIER  = 0x3b82f6;   // blue
const COLOR_OUTLIER = 0xef4444;   // red

export default class OutlierVisualizer {
  constructor({ registry, sceneRoot }) {
    this.registry = registry;
    this.sceneRoot = sceneRoot;

    // Two materials shared across every wireframe — disposed in dispose().
    // depthTest off so the wireframes are always visible regardless of
    // what's behind them. Set at construction so we don't rely on the
    // per-tile loop in _build() to do it (which used to set it on
    // whichever material got picked first, leaving the other in its
    // default state until a tile happened to need it).
    this._materialIn = new LineBasicMaterial({
      color: COLOR_INLIER,
      depthTest: false,
    });
    this._materialOut = new LineBasicMaterial({
      color: COLOR_OUTLIER,
      depthTest: false,
    });

    this.group = new Group();
    this.group.name = 'outlier_visualizer';
    this.group.visible = false;
    this.sceneRoot.add(this.group);

    this._built = false;
  }

  enable() {
    if (!this._built) this._build();
    this.group.visible = true;
  }

  disable() {
    this.group.visible = false;
  }

  toggle() {
    if (this.group.visible) this.disable();
    else this.enable();
    return this.group.visible;
  }

  _build() {
    this.registry.forEach(tile => {
      // Every tile gets a wireframe in v4 — outlier tiles have real
      // bboxes from their octree leaves now, so there's no longer a
      // "skip the bucketless outlier" case. (The legacy single-bucket
      // outlier from pre-v4 manifests still produces an infinite Box3
      // in TileRegistry — we'd build a wireframe at infinity for it,
      // which is harmless but ugly. Guard against that explicitly.)
      const min = tile.box.min;
      const max = tile.box.max;
      const finite =
        Number.isFinite(min.x) && Number.isFinite(min.y) && Number.isFinite(min.z) &&
        Number.isFinite(max.x) && Number.isFinite(max.y) && Number.isFinite(max.z);
      if (!finite) return;

      const size = new Vector3();
      const center = new Vector3();
      tile.box.getSize(size);
      tile.box.getCenter(center);

      // Edge case: degenerate box. Give it a 1ft default so it's at least
      // visible to the user when toggling on.
      if (size.x <= 0) size.x = 1;
      if (size.y <= 0) size.y = 1;
      if (size.z <= 0) size.z = 1;

      const boxGeo = new BoxGeometry(size.x, size.y, size.z);
      const edgesGeo = new EdgesGeometry(boxGeo);
      boxGeo.dispose(); // EdgesGeometry copies what it needs

      // Color by tile.isOutlier (authoritative). Red for outliers, blue
      // for inliers. See class doc for rationale.
      const mat = tile.isOutlier ? this._materialOut : this._materialIn;
      const wire = new LineSegments(edgesGeo, mat);
      wire.position.copy(center);
      wire.name = `wire_${tile.key}`;

      // Render on top of everything else when enabled — debug overlay.
      wire.renderOrder = 999;

      this.group.add(wire);
    });
    this._built = true;
  }

  dispose() {
    this.group.traverse(node => {
      if (node.isLineSegments) node.geometry.dispose();
    });
    this._materialIn.dispose();
    this._materialOut.dispose();
    if (this.group.parent) this.group.parent.remove(this.group);
  }
}